// One live call, opened from the queue: what the caller and the AI have said
// so far, the summary, and why a human was asked for. Same context an agent
// gets when the call rings them, so whoever picks up already knows the story.
//
// Read from the session rather than assembled from socket events: the session
// is the one place both providers write every turn to (Twilio as it relays,
// Exotel as the AI engine posts each turn), and it survives a missed event.
// A transcript event or a handoff change triggers a re-read; a slow poll
// covers an event bus that is down.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, ExternalLink, User, X, Headphones } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { voiceService, type VoiceSession } from '@/services/voice.service';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { useAuthStore } from '@stores/authStore';
import { fieldLabel } from '@features/voice/utils/fieldLabel';
import { TurnText } from '@features/voice/components/TurnText';
import { isClaimable } from './VoiceQueue';

/** Re-read while open, in case a live event never arrives. */
const POLL_MS = 5000;

interface VoiceSessionDetailProps {
  sessionId: string;
  onClose?: () => void;
}

export interface CallSummary {
  text?: string;
  keyIssue?: string;
  reason?: string;
}

/** The summary, from wherever this call has one. */
export function summaryOf(session: VoiceSession): CallSummary {
  let offered: { text?: string; keyIssue?: string } = {};
  const raw = session.agentCallParams?.aiSummary;
  if (raw) {
    try {
      offered = JSON.parse(raw);
    } catch {
      // Shown without it.
    }
  }
  return {
    text: session.aiSummaryText?.trim() || offered.text?.trim() || undefined,
    keyIssue: offered.keyIssue?.trim() || undefined,
    reason: session.handoffReason || session.handoff?.reason || undefined,
  };
}

export function VoiceSessionDetail({ sessionId, onClose }: VoiceSessionDetailProps) {
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const canTakeCalls = useVoiceAgentStore((s) => s.isOnline && s.isDeviceReady && !s.activeCall);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const endRef = useRef<HTMLDivElement>(null);
  const { subscribe, unsubscribe, on, off } = useWebSocket();

  const load = useCallback(async () => {
    try {
      setSession(await voiceService.getSession(sessionId));
      setError(null);
    } catch {
      setError(
        'The details of this call are not available. A call shows here once it reaches you; ' +
          'live details are kept for 24 hours, and the ticket keeps the conversation.',
      );
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    setLoading(true);
    setSession(null);
    void load();
    const poll = setInterval(load, POLL_MS);
    return () => clearInterval(poll);
  }, [load]);

  useEffect(() => {
    subscribe('subscribe_voice_session', { sessionId });
    const refresh = (data: { sessionId?: string } | undefined) => {
      // voice:transcript arrives on this session's own room and carries no id.
      if (!data?.sessionId || data.sessionId === sessionId) void load();
    };
    on('voice:transcript', refresh);
    on('voice:handoff:updated', refresh);
    on('voice:call:with_agent', refresh);
    on('voice:call:ended', refresh);
    return () => {
      unsubscribe('unsubscribe_voice_session', { sessionId });
      off('voice:transcript', refresh);
      off('voice:handoff:updated', refresh);
      off('voice:call:with_agent', refresh);
      off('voice:call:ended', refresh);
    };
  }, [sessionId, load, subscribe, unsubscribe, on, off]);

  useEffect(() => {
    if (onClose === undefined) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const turns = (session?.transcriptHistory ?? []).filter(
    (t) => t.content?.trim() && (t.speaker === 'customer' || t.speaker === 'ai' || t.speaker === 'agent'),
  );

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [turns.length]);

  const summary = session ? summaryOf(session) : {};
  const details = Object.entries(session?.collectedDetails ?? {}).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== '',
  );

  const takeCall = async () => {
    setClaiming(true);
    setClaimError(null);
    try {
      await useVoiceAgentStore.getState().takeCall(sessionId);
    } catch (err: any) {
      setClaimError(err?.response?.data?.message || 'Could not take this call');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <aside
      aria-label="Call details"
      className="flex h-full min-h-0 flex-col border-l border-[var(--co-line)] bg-[var(--co-raised)]"
    >
      {/* Header: who, where the call is, close. */}
      <div className="flex flex-none items-start justify-between gap-3 border-b border-[var(--co-line)] px-5 py-4">
        <div className="min-w-0">
          <p className="co-mono truncate text-[16px] font-bold text-[var(--co-ink)]">
            {session ? voiceService.formatPhoneNumber(session.from) || 'Unknown number' : '—'}
          </p>
          {session && (
            <p className="co-mono mt-[5px] text-[10px] uppercase tracking-[.12em] text-[var(--co-ink-3)]">
              {voiceService.getStatusLabel(session.status)} ·{' '}
              {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })} · {turns.length} messages
            </p>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close call details"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[var(--co-ink-3)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {loading && !session ? (
        <p className="p-8 text-center text-[12.5px] text-[var(--co-ink-3)]">Loading call…</p>
      ) : !session ? (
        <p className="p-8 text-center text-[12.5px]" style={{ color: 'var(--co-burgundy)' }}>
          {error}
        </p>
      ) : (
        <div className="co-scrollbar-none min-h-0 flex-1 overflow-y-auto">
          {/* What the agent needs before saying hello. */}
          <section className="space-y-3 border-b border-[var(--co-line-subtle)] px-5 py-4">
            <Field label="Summary">
              {summary.text ?? (
                <span className="text-[var(--co-ink-3)]">
                  No summary yet — the conversation is below.
                </span>
              )}
            </Field>
            {summary.keyIssue && summary.keyIssue !== summary.reason && (
              <Field label="Key issue">{summary.keyIssue}</Field>
            )}
            {summary.reason && <Field label="Asked for a person because">{summary.reason}</Field>}
            {session.ticketTypeName && <Field label="Request">{session.ticketTypeName}</Field>}
            {details.length > 0 && (
              <Field label="Already collected">
                <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-2">
                  {details.map(([key, value]) => (
                    <div key={key} className="min-w-0">
                      <dt className="truncate text-[11px] text-[var(--co-ink-3)]">{fieldLabel(key)}</dt>
                      <dd className="truncate text-[12.5px] font-medium">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              </Field>
            )}
            {isClaimable(session, currentUserId) && (
              <div>
                <button
                  type="button"
                  onClick={takeCall}
                  disabled={!canTakeCalls || claiming}
                  title={canTakeCalls ? 'Answer this caller now' : 'Go online to take calls'}
                  className="flex h-[34px] items-center rounded-lg px-[14px] text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ background: 'var(--co-brass-ink)' }}
                >
                  {claiming ? 'Connecting…' : 'Accept call'}
                </button>
                {claimError && (
                  <p role="alert" className="mt-1 text-[12px]" style={{ color: 'var(--co-burgundy)' }}>
                    {claimError}
                  </p>
                )}
              </div>
            )}
            {session.handledByName && <Field label="Handled by">{session.handledByName}</Field>}
            {(session.ticket || session.ticketId) && (
              <Field label="Ticket">
                <a
                  href={`/tickets/${session.ticket?.id ?? session.ticketId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-[6px] font-semibold text-[var(--co-brass-ink)] hover:underline"
                >
                  <ExternalLink className="h-[13px] w-[13px]" />
                  {session.ticket?.ticketNumber ?? 'Open ticket'}
                </a>
                {session.ticket && (
                  <span className="ml-2 text-[var(--co-ink-3)]">
                    {session.ticket.assigneeName ? `Assigned to ${session.ticket.assigneeName}` : 'Unassigned'}
                  </span>
                )}
              </Field>
            )}
          </section>

          {/* The conversation so far. */}
          <section className="px-5 py-4">
            <h3 className="co-mono mb-3 text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              Conversation
            </h3>
            {session.conversationRestricted ? (
              <p className="py-6 text-center text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">
                The caller is waiting for an agent. What they told the AI shows here once you take the call.
              </p>
            ) : turns.length === 0 ? (
              <p className="py-6 text-center text-[12.5px] text-[var(--co-ink-3)]">
                Nothing said yet.
              </p>
            ) : (
              <ol className="space-y-3">
                {turns.map((turn, i) => {
                  const fromCaller = turn.speaker === 'customer';
                  const fromAgent = turn.speaker === 'agent';
                  return (
                    <li key={`${turn.timestamp}-${i}`} className={fromCaller ? 'flex' : 'flex flex-row-reverse'}>
                      <div className={`flex max-w-[85%] gap-2 ${fromCaller ? '' : 'flex-row-reverse'}`}>
                        <span
                          className="mt-[2px] flex h-6 w-6 flex-none items-center justify-center rounded-full"
                          style={
                            fromCaller
                              ? { background: 'var(--co-sunken)', color: 'var(--co-ink-3)' }
                              : fromAgent
                                ? { background: 'var(--co-brass-wash)', color: 'var(--co-brass-ink)' }
                                : { background: 'var(--co-machine-wash)', color: 'var(--co-machine-ink)' }
                          }
                        >
                          {fromCaller ? (
                            <User className="h-3 w-3" />
                          ) : fromAgent ? (
                            <Headphones className="h-3 w-3" />
                          ) : (
                            <Bot className="h-3 w-3" />
                          )}
                        </span>
                        <div>
                          <p
                            className={`co-mono text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)] ${fromCaller ? '' : 'text-right'}`}
                          >
                            {fromCaller ? 'Caller' : fromAgent ? turn.speakerName || 'Agent' : 'AI'}
                          </p>
                          <div
                            className="mt-[3px] rounded-[10px] px-3 py-2 text-[12.5px] leading-[1.45] text-[var(--co-ink)]"
                            style={{
                              background: fromCaller
                                ? 'var(--co-sunken)'
                                : fromAgent
                                  ? 'var(--co-brass-wash)'
                                  : 'var(--co-machine-wash)',
                            }}
                          >
                            <TurnText content={turn.content} language={turn.language} english={turn.contentEnglish} />
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div ref={endRef} />
          </section>
        </div>
      )}
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">{label}</p>
      <div className="mt-[4px] text-[12.5px] leading-[1.45] text-[var(--co-ink)]">{children}</div>
    </div>
  );
}

export default VoiceSessionDetail;
